import type { HomeyAPI, NotificationOwner } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { defer } from '../../../lib/defer';
import { PendingError } from '../../../lib/InternalError';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { KeyedAsyncQueue } from '../../../store/KeyedAsyncQueue';
import { OptimisticEntityUpdates } from '../../../store/OptimisticEntityUpdates';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type OwnerData = Record<string, NotificationOwner>;
type OwnerUpdate = { enabled: boolean } | { push: boolean };

interface State {
  ownersData: null | OwnerData;
  isLoading: boolean;
  error: unknown;
}

export type NotificationSettingsStoreState = State;

export class NotificationSettingsStore extends BaseApiStore<State> {
  #optimisticUpdates = new OptimisticEntityUpdates<NotificationOwner, OwnerUpdate>({
    applyOperation: NotificationSettingsStore.#applyOwnerUpdate,
    reconcileMutationResult(baseOwner, mutationOwner, operation) {
      return NotificationSettingsStore.#applyOwnerUpdate(baseOwner, operation);
    },
  });
  #persistenceQueue = new KeyedAsyncQueue<string>();

  constructor() {
    super('notification-owners-settings');
  }

  override createInitialState() {
    return {
      ownersData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const baseRevision = this.#optimisticUpdates.captureBaseRevision();
      const ownersData = await api.notifications.getOwners();

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        ownersData: this.#optimisticUpdates.reconcileBaseSnapshot(baseRevision, ownersData),
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {
    this.#optimisticUpdates.clear();
    this.#persistenceQueue.clear();
    this.pending = {};
  }

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'notification-owners-settings');

    return { notificationSettings: this.store(selectAll, shallow) };
  }

  async setOwnerEnabled({ uri, enabled }: { uri: string; enabled: boolean }) {
    await this.#updateOwner(uri, { enabled });
  }

  async setOwnerPush({ uri, push }: { uri: string; push: boolean }) {
    await this.#updateOwner(uri, { push });
  }

  async #updateOwner(uri: string, operation: OwnerUpdate) {
    const field = 'enabled' in operation ? 'enabled' : 'push';
    const key = `${field}:${uri}`;

    if (this.pending[key] != null) {
      throw new PendingError();
    }

    const api = this.getApi();
    const owner = this.get().ownersData?.[uri];

    if (owner == null) {
      throw new Error('Cannot update notification settings for a missing owner.');
    }

    const pending = defer<void>();
    this.pending[key] = pending;

    try {
      const { mutationId, entity } = this.#optimisticUpdates.beginMutation(uri, owner, operation);
      this.#publishOwner(uri, entity);

      await this.#persistenceQueue.enqueue(uri, async () => {
        this.#optimisticUpdates.startPersistence(uri, mutationId);

        try {
          if ('enabled' in operation) {
            await api.notifications.setOwnerEnabled({ uri, enabled: operation.enabled });
          } else {
            await api.notifications.setOwnerPush({ uri, push: operation.push });
          }
        } catch (error) {
          const result = this.#optimisticUpdates.finishMutation(uri, mutationId, {
            status: 'failure',
          });

          if (result.status === 'applied') {
            this.#publishOwner(uri, result.entity);
          }

          throw error;
        }

        const result = this.#optimisticUpdates.finishMutation(uri, mutationId, {
          status: 'success',
        });

        if (result.status === 'applied') {
          this.#publishOwner(uri, result.entity);
        }
      });
    } finally {
      pending.resolve();

      if (this.pending[key] === pending) {
        delete this.pending[key];
      }
    }
  }

  #publishOwner(uri: string, owner: NotificationOwner | undefined) {
    const state = this.get();

    if (state.ownersData == null) {
      return;
    }

    const ownersData = { ...state.ownersData };

    if (owner == null) {
      delete ownersData[uri];
    } else {
      ownersData[uri] = owner;
    }

    this.set({ ownersData });
  }

  static #applyOwnerUpdate(owner: NotificationOwner | undefined, operation: OwnerUpdate) {
    if (owner == null) {
      return undefined;
    }

    return { ...owner, ...operation };
  }

  async deleteNotifications({ uri }: { uri: string }) {
    const api = this.getApi();
    await api.notifications.deleteNotifications({ ownerUri: uri });
  }
}

function selectAll(state: NotificationSettingsStoreState) {
  return {
    ...selectBase(state),
    ownersData: state.ownersData,
  };
}

export const notificationSettingsStore = new NotificationSettingsStore();
